package dev.kuml.portal.web.components

import dev.kuml.portal.web.i18n.Language
import dev.kuml.portal.web.i18n.LanguageState
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.html.Button
import io.kvision.html.button
import io.kvision.html.div

/**
 * R-18's `DE | EN` two-segment language toggle -- see this wave's plan §4 for why a toggle over a
 * `<select>` (both values are always visible at exactly two options; no dropdown click for zero
 * additional information) and §4.2 for why it is mounted in THREE places
 * ([dev.kuml.portal.web.components.portalHeader], [dev.kuml.portal.web.auth.authScreen],
 * [dev.kuml.portal.web.legal.impressumScreen]/`datenschutzScreen`/`agbScreen`) rather than only the
 * header -- each of those three screens is reachable WITHOUT an authenticated header being on
 * screen at all.
 *
 * Clicking [Language.set] triggers [dev.kuml.portal.web.PortalApp]'s `onLanguageChanged` callback,
 * which rebuilds the currently mounted screen wholesale (see `PortalApp.renderScreen`'s KDoc) --
 * this widget itself does nothing beyond calling [LanguageState.set]; it never diffs its own
 * `active` class in place (there IS no "in place" -- the whole screen, including this widget, is
 * about to be torn down and rebuilt).
 *
 * Labels are deliberately NEVER translated -- `Deutsch`/`English` as the `title`, `DE`/`EN` as the
 * visible label -- a German label for "English" helps exactly nobody who needs to switch TO
 * English.
 */
public fun Container.languageSwitcher() {
    lateinit var deButton: Button
    lateinit var enButton: Button

    fun applyActive() {
        val current = LanguageState.current
        deButton.setAttribute("aria-pressed", (current == Language.DE).toString())
        enButton.setAttribute("aria-pressed", (current == Language.EN).toString())
        if (current == Language.DE) {
            deButton.addCssClass("kportal-lang-option-active")
            enButton.removeCssClass("kportal-lang-option-active")
        } else {
            enButton.addCssClass("kportal-lang-option-active")
            deButton.removeCssClass("kportal-lang-option-active")
        }
    }

    div(className = "kportal-lang-switch") {
        setAttribute("role", "group")
        setAttribute("aria-label", t.common.languageSwitcherAriaLabel)

        deButton = button(Language.DE.shortLabel, className = "kportal-lang-option")
        deButton.setAttribute("lang", "de")
        deButton.setAttribute("title", Language.DE.nativeLabel)
        deButton.onClick { LanguageState.set(Language.DE) }

        div("·", className = "kportal-lang-sep")

        enButton = button(Language.EN.shortLabel, className = "kportal-lang-option")
        enButton.setAttribute("lang", "en")
        enButton.setAttribute("title", Language.EN.nativeLabel)
        enButton.onClick { LanguageState.set(Language.EN) }
    }

    applyActive()
}
